/**
 * features/hr/AttendanceCalendar.tsx
 * ------------------------------------------------------------------
 * Month-at-a-glance attendance grid for one staff member.
 *
 *   - 7-column grid (Mon–Sun), exact month shown
 *   - prev/next month navigation
 *   - weekend cells visually dimmed
 *   - Botswana public holiday cells dimmed + show holiday name
 *   - working days: in/out times when present, badge when
 *     late / absent / on leave
 *   - totals strip at the bottom: working days, present, late,
 *     absent, on leave, total hours
 *
 * Pure-presentational — takes an array of HrAttendance rows for one
 * staff member plus optional approved-leave dates, and renders.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { HrAttendance } from '@/lib/hr';
import { isWeekend } from '@/lib/holidays';
import { usePublicHolidays } from '@/features/hr/usePublicHolidays';
import { dayVerdict, fmtMinutes, fmtWorked, SCHOOL_CLOCKING, type ClockingRules } from '@/lib/clockingRules';

interface Props {
  attendance: HrAttendance[];
  /** Approved-leave date keys (YYYY-MM-DD) for this staff. */
  leaveDates?: Set<string>;
  /** This person's clocking rules (their staff type's start times). */
  rules?: ClockingRules;
}

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The calendar date of a LOCAL date, YYYY-MM-DD. Not toISOString(): in
 *  Botswana (UTC+2) local midnight is 22:00 the day before in UTC, which put
 *  every record one cell late — Monday's punches on Tuesday. */
function dayStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** Saturday counts as a working day for anyone who clocked in on it. */
function offDay(d: Date, rec: HrAttendance | undefined): boolean {
  if (!isWeekend(d)) return false;
  return !(d.getDay() === 6 && rec && rec.punches.length > 0);
}
function monthLabel(year: number, monthIdx: number): string {
  return new Date(year, monthIdx, 1).toLocaleDateString([], { month: 'long', year: 'numeric' });
}

export default function AttendanceCalendar({ attendance, leaveDates, rules = SCHOOL_CLOCKING }: Props) {
  const today = new Date();
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const { holidayName } = usePublicHolidays();

  /** Map YYYY-MM-DD -> HrAttendance for fast lookup */
  const byDate = useMemo(() => {
    const m = new Map<string, HrAttendance>();
    for (const a of attendance) m.set(a.date, a);
    return m;
  }, [attendance]);

  /** Build the grid: starts on Monday of the week containing day 1. */
  const cells = useMemo(() => {
    const first = new Date(view.year, view.month, 1);
    // JS getDay: Sun=0..Sat=6. We want Mon-first.
    const dowFromMon = (first.getDay() + 6) % 7;
    const gridStart = new Date(view.year, view.month, 1 - dowFromMon);
    const rows: Date[][] = [];
    let d = new Date(gridStart);
    // Always render 6 rows (42 cells) so layout is stable, but trim
    // the trailing empty week if it has no in-month days.
    for (let r = 0; r < 6; r++) {
      const week: Date[] = [];
      for (let c = 0; c < 7; c++) {
        week.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }
      rows.push(week);
    }
    return rows;
  }, [view.year, view.month]);

  /** Monthly totals for the strip at the bottom. */
  const totals = useMemo(() => {
    let workingDays = 0;
    let present = 0;
    let late = 0;
    let absent = 0;
    let leave = 0;
    let totalHours = 0;
    for (let day = 1; day <= 31; day++) {
      const d = new Date(view.year, view.month, day);
      if (d.getMonth() !== view.month) break;
      const key = dayStr(d);
      const a = byDate.get(key);
      if (offDay(d, a)) continue;
      if (holidayName(key)) continue;
      if (!isWeekend(d)) workingDays += 1;
      if (a) {
        // Judged from the punches (lib/clockingRules), not the stored status.
        const v = dayVerdict(a, rules);
        if (v.status === 'present') present += 1;
        else if (v.status === 'late') { late += 1; present += 1; }
        else if (v.status === 'absent') absent += 1;
        else if (v.status === 'leave') leave += 1;
        totalHours += (v.minutesWorked ?? 0) / 60;
      } else if (leaveDates?.has(key)) {
        leave += 1;
      } else if (d <= today) {
        // No attendance record on a past working day = effective absent
        // for the purposes of the totals (don't penalise future days)
      }
    }
    return { workingDays, present, late, absent, leave, totalHours };
  }, [view.year, view.month, byDate, leaveDates, today, holidayName]);

  function prev() {
    setView((v) => ({
      year: v.month === 0 ? v.year - 1 : v.year,
      month: (v.month + 11) % 12,
    }));
  }
  function next() {
    setView((v) => ({
      year: v.month === 11 ? v.year + 1 : v.year,
      month: (v.month + 1) % 12,
    }));
  }
  function thisMonth() {
    setView({ year: today.getFullYear(), month: today.getMonth() });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold text-gray-800 dark:text-gray-100">
          {monthLabel(view.year, view.month)}
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={prev}
            aria-label="Previous month"
            className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={thisMonth}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg dark:text-gray-300 dark:hover:bg-gray-700"
          >
            This month
          </button>
          <button
            onClick={next}
            aria-label="Next month"
            className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* DOW header */}
      <div className="grid grid-cols-7 gap-1.5">
        {DOW_LABELS.map((d) => (
          <div
            key={d}
            className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 text-center font-medium py-1"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1.5">
        {cells.flat().map((d, i) => {
          const inMonth = d.getMonth() === view.month;
          const key = dayStr(d);
          const hol = holidayName(key);
          const rec = byDate.get(key);
          const weekend = offDay(d, rec);
          const onLeave = leaveDates?.has(key) ?? false;
          const isToday = dayStr(d) === dayStr(today);
          const isFuture = d > today;
          return (
            <CalendarCell
              key={i}
              date={d}
              inMonth={inMonth}
              weekend={weekend}
              holiday={rec?.punches.length ? undefined : hol}
              attendance={rec}
              onLeave={onLeave}
              isToday={isToday}
              isFuture={isFuture}
              rules={rules}
            />
          );
        })}
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <Totals label="Working days" value={totals.workingDays} />
        <Totals label="Present" value={totals.present} tone="emerald" />
        <Totals label="Late" value={totals.late} tone="gold" />
        <Totals label="Absent" value={totals.absent} tone="red" />
        <Totals label="On leave" value={totals.leave} tone="blue" />
        <Totals label="Hours" value={totals.totalHours.toFixed(1)} />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[11px] text-gray-500 dark:text-gray-400 pt-1">
        <Legend swatch="bg-emerald-100 dark:bg-emerald-900/40" label="Present" />
        <Legend swatch="bg-amber-100 dark:bg-amber-900/40" label="Late" />
        <Legend swatch="bg-red-100 dark:bg-red-900/40" label="Absent" />
        <Legend swatch="bg-blue-100 dark:bg-blue-900/40" label="On leave" />
        <Legend swatch="bg-gray-100 dark:bg-gray-700/60" label="Weekend / holiday" />
      </div>
    </div>
  );
}

function CalendarCell({
  date,
  inMonth,
  weekend,
  holiday,
  attendance,
  onLeave,
  isToday,
  isFuture,
  rules,
}: {
  rules: ClockingRules;
  date: Date;
  inMonth: boolean;
  weekend: boolean;
  holiday: string | undefined;
  attendance?: HrAttendance;
  onLeave: boolean;
  isToday: boolean;
  isFuture: boolean;
}) {
  const day = date.getDate();
  const baseRing = isToday
    ? 'ring-2 ring-[var(--accent-link,#2d5295)] dark:ring-blue-400'
    : '';
  const dim = !inMonth
    ? 'opacity-30'
    : '';

  // Non-working day (weekend or holiday): blocked out
  if (weekend || holiday) {
    return (
      <div
        title={holiday || (weekend ? 'Weekend' : '')}
        className={`relative rounded-lg p-2 h-[68px] bg-gray-100 dark:bg-gray-700/40 ${dim} ${baseRing}`}
      >
        <div className="text-[11px] font-medium text-gray-400 dark:text-gray-500">
          {day}
        </div>
        {holiday && (
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 leading-tight line-clamp-2">
            {holiday}
          </div>
        )}
        {weekend && !holiday && (
          <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
            Weekend
          </div>
        )}
      </div>
    );
  }

  // Future working day, no data yet
  if (isFuture && !attendance) {
    return (
      <div
        className={`rounded-lg p-2 h-[68px] border border-dashed border-gray-200 dark:border-gray-700 ${dim} ${baseRing}`}
      >
        <div className="text-[11px] font-medium text-gray-400 dark:text-gray-500">
          {day}
        </div>
      </div>
    );
  }

  // Working day with status — judged from the punches (lib/clockingRules).
  const v = attendance ? dayVerdict(attendance, rules) : null;
  const status = v?.status;
  let bg = 'bg-white dark:bg-gray-800';
  let border = 'border border-gray-100 dark:border-gray-700';
  let bodyTone = '';
  let label: string | null = null;

  if (status === 'present') {
    bg = 'bg-emerald-50 dark:bg-emerald-900/20';
    border = 'border border-emerald-100 dark:border-emerald-900/40';
    bodyTone = 'text-emerald-800 dark:text-emerald-300';
  } else if (status === 'late') {
    bg = 'bg-amber-50 dark:bg-amber-900/20';
    border = 'border border-amber-100 dark:border-amber-900/40';
    bodyTone = 'text-amber-800 dark:text-amber-300';
  } else if (status === 'absent') {
    bg = 'bg-red-50 dark:bg-red-900/20';
    border = 'border border-red-100 dark:border-red-900/40';
    bodyTone = 'text-red-800 dark:text-red-300';
    label = 'Absent';
  } else if (status === 'leave' || onLeave) {
    bg = 'bg-blue-50 dark:bg-blue-900/20';
    border = 'border border-blue-100 dark:border-blue-900/40';
    bodyTone = 'text-blue-800 dark:text-[var(--accent-link-dark,#93c5fd)]';
    label = 'On leave';
  } else {
    // Past working day with no record
    bg = 'bg-gray-50 dark:bg-gray-700/30';
    border = 'border border-gray-100 dark:border-gray-700';
    bodyTone = 'text-gray-500 dark:text-gray-400';
    label = '—';
  }

  const title = v?.missingIn
    ? `No clock-in · Out ${v.lastOut}`
    : v?.firstIn
    ? [`In ${v.firstIn}`, v.lastOut ? `Out ${v.lastOut}` : 'No clock-out',
      v.minutesWorked !== null ? `worked ${fmtWorked(v)}` : '', v.late ? `late ${fmtMinutes(v.lateMinutes)}` : 'on time']
      .filter(Boolean).join(' · ')
    : undefined;

  return (
    <div className={`rounded-lg p-2 h-[68px] ${bg} ${border} ${baseRing}`} title={title}>
      <div className="flex items-center justify-between gap-1">
        <div className={`text-[11px] font-medium ${bodyTone}`}>{day}</div>
        {v?.late && (
          <span className="text-[9px] font-bold tracking-wide text-amber-700 dark:text-amber-300 whitespace-nowrap">
            +{v.lateMinutes < 60 ? `${v.lateMinutes}m` : fmtMinutes(v.lateMinutes)}
          </span>
        )}
      </div>
      {v?.firstIn || v?.lastOut ? (
        <div className={`text-[10px] mt-1 ${bodyTone} leading-tight`}>
          <div>{v.firstIn ?? 'no in'}</div>
          <div className="opacity-70">{v.lastOut ?? 'no out'}</div>
        </div>
      ) : (
        <div className={`text-[10px] mt-1 font-medium ${bodyTone}`}>{label}</div>
      )}
    </div>
  );
}

function Totals({ label, value, tone }: { label: string; value: number | string; tone?: 'emerald' | 'gold' | 'red' | 'blue' }) {
  const toneCls =
    tone === 'emerald' ? 'text-emerald-700 dark:text-emerald-300'
    : tone === 'gold' ? 'text-amber-700 dark:text-amber-300'
    : tone === 'red' ? 'text-red-700 dark:text-red-300'
    : tone === 'blue' ? 'text-blue-700 dark:text-[var(--accent-link-dark,#93c5fd)]'
    : 'text-gray-800 dark:text-gray-100';
  return (
    <div className="text-center">
      <div className={`text-lg font-semibold ${toneCls}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {label}
      </div>
    </div>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 rounded ${swatch}`} />
      {label}
    </div>
  );
}
